import React, {Component} from 'react';
import strings from 'utils/strings';

const normalize = (s = '') => s.replace(/[.,]/g, '').trim();
const toLower = (s = '') => normalize(s).toLowerCase();

// ---- child-screening subtest 2 helpers ----
const tokenize = (s = '') => normalize(s).split(/\s+/).filter(Boolean);

function buildRemainingCounts(words = []) {
	const m = Object.create(null);
	for (const w of words) {
		const k = w.toLowerCase();
		m[k] = (m[k] || 0) + 1;
	}
	return m;
}

function scoreAndClassifyUserTokens(userTokens, correctTokens) {
	const remaining = buildRemainingCounts(correctTokens);
	let matched = 0;

	const states = userTokens.map((t) => {
		const k = t.toLowerCase();
		if (remaining[k] > 0) {
			remaining[k] -= 1;
			matched += 1;
			return 'correct';
		}
		return 'wrong';
	});

	return {matched, states};
}

function pickBestCorrectTokens(userTokens, correctValues = []) {
	let best = {matched: -1, correctTokens: []};

	for (const cv of correctValues) {
		const cTokens = tokenize(cv);
		const res = scoreAndClassifyUserTokens(userTokens, cTokens);
		if (res.matched > best.matched) {
			best = {matched: res.matched, correctTokens: cTokens};
		}
	}

	return best.correctTokens;
}

function classifyAnswer(rawValue = '', correctValues = []) {
	const ans = normalize(rawValue);
	if (!ans) return {state: 'empty', text: ''};

	const exactHit = correctValues.some((v) => normalize(v) === ans);
	if (exactHit) return {state: 'correct', text: ans};

	const caseOnly = correctValues.some(
		(v) => toLower(v) === ans.toLowerCase()
	);
	if (caseOnly) return {state: 'case', text: rawValue}; // preserve user casing

	return {state: 'wrong', text: ans};
}

class SubtestAnswers extends Component {
	constructor(props) {
		super(props);

		this.state = {
			viewMode: 'SHOW_ANSWERED',
		};
	}

	setViewMode(value) {
		this.setState({
			viewMode: value,
		});
	}

	toggleShowUnanswered() {
		if (this.state.viewMode === 'SHOW_ALL') {
			this.setViewMode('SHOW_ANSWERED');
		} else {
			this.setViewMode('SHOW_ALL');
		}
	}
	renderRepeatAnswer(repeatAnswer, key) {
		return (
			<span className="sub-test__answer--wrong" key={key}>
				<strike>{repeatAnswer + ' '}</strike>
			</span>
		);
	}

	checkWordWithArray(word, array, key) {
		const answer = word
			.replace(/\,/g, '') //eslint-disable-line
			.replace(/\./g, '') //eslint-disable-line
			.trim()
			.toLowerCase();
		for (let i = 0; i < array.length; i++) {
			const correct = array[i].trim().toLowerCase();
			if (answer === correct) {
				return (
					<span className="sub-test__answer--correct" key={key}>
						{answer + ' '}
					</span>
				);
			}
		}
		return (
			<span className="sub-test__answer--wrong" key={key}>
				{answer + ' '}
			</span>
		);
	}

	renderAnswerTable(title, rows, className = '') {
		return (
			<div className={`sub-test-answer-table-wrap ${className}`}>
				{title && (
					<h5 className="sub-test-answer-table__title">{title}</h5>
				)}

				<table className="sub-test-answer-table">
					<thead>
						<tr>
							<th>Nr</th>
							<th>Svar</th>
							<th>Korrekt svar</th>
							<th>Resultat</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((row) => (
							<tr
								key={row.id}
								className={
									row.isCorrect
										? 'is-correct'
										: row.isEmpty
											? 'is-empty'
											: 'is-wrong'
								}
							>
								<td>{row.id}</td>
								<td>{row.answer || strings.notanswered}</td>
								<td>{row.correct}</td>
								<td className="sub-test-answer-table__result">
									{row.isCorrect ? '✓' : '✕'}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		);
	}

	renderQuestionTable(title, rows) {
		return (
			<div className="sub-test-question-table-wrap">
				{title && (
					<h5 className="sub-test-answer-table__title">{title}</h5>
				)}

				<table className="sub-test-question-table">
					<thead>
						<tr>
							<th>Nr</th>
							<th>Fråga</th>
							<th>Svar</th>
							<th>Resultat</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((row) => (
							<tr
								key={row.id}
								className={
									row.isCorrect
										? 'is-correct'
										: row.isEmpty
											? 'is-empty'
											: 'is-wrong'
								}
							>
								<td>{row.id}</td>
								<td>{row.question}</td>
								<td>{row.answer || strings.notanswered}</td>
								<td className="sub-test-answer-table__result">
									{row.isCorrect ? '✓' : '✕'}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		);
	}

	render() {
		const {subtest, testType} = this.props;
		const {viewMode} = this.state;
		const {formattedAnswers, answersCorrect} = subtest;

		let notAnswered = 0;
		let answersClass = '';
		let isVisible = true;
		switch (viewMode) {
			case 'SHOW_ALL':
				answersClass = 'sub-test__answers--show-not-answered';
				break;
			case 'SHOW_ANSWERED':
				answersClass = '';
				break;
			case 'HIDE_ALL':
			default: {
				isVisible = false;
			}
		}

		switch (subtest.id) {
			case 1:
			case 3:
			case 6: {
				answersClass += ' col-4';
				break;
			}
			case 4: {
				answersClass += ' col-1';
				break;
			}
			case 5: {
				answersClass += ' col-2';
				break;
			}
			default:
				break;
		}

		const getCorrectClass = (isCorrect) => {
			return `sub-test__answer--${isCorrect ? 'correct' : 'wrong'}`;
		};
		const renderSubTest1Answers = (answer, index) => {
			const correctClass = getCorrectClass(answer.isCorrect);

			return (
				<div className={`sub-test__answer`} key={index}>
					<p>
						{answer.id + '. '}
						<span className={correctClass}>{answer.value}</span>
					</p>
					{answer.correctValues.map((correctValue) => (
						<p key={correctValue}>
							{strings.formatString(
								strings.rightanswer,
								`${correctValue}`
							)}
						</p>
					))}
				</div>
			);
		};
		const renderSubTest2Answers = (answer, index) => {
			let wrapperClass = 'sub-test__answer';
			// "not answered" rule for this task: they didn't add any spaces

			if (!answer.value || answer.value.indexOf(' ') === -1) {
				notAnswered++;
				wrapperClass += ' sub-test__answer--not-answered';
				return (
					<div className={wrapperClass} key={index}>
						<p>
							{answer.id + '. '}
							<span className="sub-test__answer--wrong">
								{strings.notanswered}
							</span>
						</p>

						{answer.correctValues.map((correctValue) => (
							<p key={correctValue}>
								{strings.formatString(
									strings.rightanswer,
									`${correctValue}`
								)}
							</p>
						))}
					</div>
				);
			}

			// ✅ Special display ONLY for child-screening subtest 2
			if (testType === 'child-screening') {
				const userTokens = tokenize(answer.value);

				// Pick best correct chain (usually there’s only one)
				const bestCorrectTokens = pickBestCorrectTokens(
					userTokens,
					answer.correctValues
				);

				const {matched, states} = scoreAndClassifyUserTokens(
					userTokens,
					bestCorrectTokens
				);
				return (
					<div className={wrapperClass} key={index}>
						<p>
							{answer.id + '. '}
							<span>
								{userTokens.map((t, i) => (
									<span
										key={`${answer.id}-${i}-${t}`}
										className={
											states[i] === 'correct'
												? 'sub-test__answer--correct'
												: 'sub-test__answer--wrong'
										}
									>
										{t.toUpperCase() + ' '}
									</span>
								))}
							</span>
							<span className="sub-test__answer__points">{`(${matched}/4)`}</span>
						</p>

						{answer.correctValues.map((correctValue) => (
							<p key={correctValue}>
								{strings.formatString(
									strings.rightanswer,
									`${correctValue}`
								)}
							</p>
						))}
					</div>
				);
			}

			// Existing behavior for all other test types
			const correctClass = getCorrectClass(answer.isCorrect);
			return (
				<div className={wrapperClass} key={index}>
					<p>
						{answer.id + '. '}
						<span className={correctClass}>{answer.value}</span>
					</p>
					{answer.correctValues.map((correctValue) => (
						<p key={correctValue}>
							{strings.formatString(
								strings.rightanswer,
								`${correctValue}`
							)}
						</p>
					))}
				</div>
			);
		};

		const renderSubTest3Answers = (answer, index) => {
			let wrapperClass = 'sub-test__answer';
			const correctClass = getCorrectClass(answer.isCorrect);
			let answerText = answer.value;
			if (!answer.value) {
				notAnswered++;
				answerText = strings.notanswered;
				wrapperClass += ' sub-test__answer--not-answered';
			}
			return (
				<div className={wrapperClass} key={index}>
					<p>
						{answer.id + '. '}
						<span className={correctClass}>{answerText}</span>
					</p>
					{answer.correctValues.map((correctValue) => (
						<p key={correctValue}>
							{strings.formatString(
								strings.rightanswer,
								`${correctValue}`
							)}
						</p>
					))}
				</div>
			);
		};
		const renderSubTest4Answers = (answers, index) => {
			const isEvaluated = [];
			const spanArray = [];
			const repeatExceptions = {
				är: 2,
				att: 2,
			};

			// eslint-disable-next-line no-lone-blocks
			{
				answers.map((subanswer, index) => {
					if (
						!isEvaluated.includes(
							subanswer.value
								.replace(/\,/g, '')
								.replace(/\./g, '')
								.trim()
								.toLowerCase()
						)
					) {
						if (
							!repeatExceptions.hasOwnProperty(
								subanswer.value.toLowerCase()
							)
						) {
							isEvaluated.push(subanswer.value.toLowerCase());
						}
						if (
							repeatExceptions.hasOwnProperty(
								subanswer.value.toLowerCase()
							)
						) {
							repeatExceptions[subanswer.value.toLowerCase()] =
								repeatExceptions[
									subanswer.value.toLowerCase()
								] - 1;
							if (
								repeatExceptions[
									subanswer.value.toLowerCase()
								] === 0
							) {
								isEvaluated.push(subanswer.value.toLowerCase());
							}
						}

						spanArray.push(
							this.checkWordWithArray(
								subanswer.value,
								answersCorrect,
								index
							)
						);
					} else {
						spanArray.push(
							this.renderRepeatAnswer(subanswer.value, index)
						);
					}
				});
			}
			return (
				<li className="sub-test__answer__li" key={`${index}`}>
					{spanArray.map((spans) => {
						return spans;
					})}
				</li>
			);
		};
		const renderSubTest5Answers = (answer, index) => {
			let wrapperClass = 'sub-test__answer';
			const correctClass = getCorrectClass(answer.isCorrect);
			let answerText = '';
			if (answer.choices && answer.value) {
				answerText = answer.choices[answer.value].choice;
			}

			if (!answer.value) {
				notAnswered++;
				answerText = strings.notanswered;
				wrapperClass += ' sub-test__answer--not-answered';
			}

			return (
				<div className={wrapperClass} key={index}>
					<p>
						{`${answer.id}. `}
						{answer.question ? answer.question : ''}
					</p>
					<p className={correctClass}>{answerText}</p>
				</div>
			);
		};
		const renderSubTest6Answers = (answer, index) => {
			let wrapperClass = 'sub-test__answer';
			const correctClass = getCorrectClass(answer.isCorrect);

			let answerText = '';

			if (!answer.value) {
				notAnswered++;
				answerText = strings.notanswered;
				wrapperClass += ' sub-test__answer--not-answered';
			}

			if (answer.choices && answer.value) {
				answerText = answer.choices[answer.value].choice;
			}
			wrapperClass += ' sub-test__answer--compact ';

			return (
				<div className={wrapperClass} key={index}>
					<p>
						{`${answer.id}. `}
						{answer.question ? answer.question : ''}
					</p>
					<p>
						<span className={correctClass}>{answerText}</span>
					</p>
				</div>
			);
		};

		let answers;

		if ([1, 2, 3].includes(subtest.id)) {
			const rows = formattedAnswers.map((answer) => {
				const correct = answer.correctValues?.[0] || '';
				const isEmpty = !answer.value;

				return {
					id: answer.id,
					answer: isEmpty ? '' : answer.value,
					correct,
					isCorrect: answer.isCorrect,
					isEmpty,
				};
			});

			answers = this.renderAnswerTable(null, rows);
		} else if ([5, 6].includes(subtest.id)) {
			const rows = formattedAnswers.map((answer) => {
				let answerText = '';

				if (answer.choices && answer.value) {
					answerText = answer.choices[answer.value].choice;
				}

				return {
					id: answer.id,
					question: answer.question || '',
					answer: answerText,
					isCorrect: answer.isCorrect,
					isEmpty: !answer.value,
				};
			});

			answers = this.renderQuestionTable(null, rows);
		} else if (subtest.id === 4) {
			answers = (
				<ol className="sub-test__answer__ol">
					{formattedAnswers.map((answer, index) =>
						renderSubTest4Answers(answer, index)
					)}
				</ol>
			);
		}

		return (
			isVisible &&
			formattedAnswers.length && (
				<div>
					<h4 className="sub-test__answers__heading">
						{strings.candidate_answers}
					</h4>
					<div
						className={`sub-test__answers sub-test__answers--table ${answersClass}`}
					>
						{answers}
					</div>
					{notAnswered > 0 && (
						<p className="sub-test__not-answered">
							<b>
								{strings.formatString(
									strings.testnotanswered,
									`${notAnswered}`
								)}
							</b>
							{/* <b>{`${notAnswered} oppgaver ikke besvart`}</b>{' '} */}
							<button
								className="sub-test__link lit-link-button print-hide"
								onClick={(e) => {
									e.preventDefault();
									this.toggleShowUnanswered(subtest.id);
								}}
							>
								{viewMode === 'SHOW_ALL'
									? strings.hideunanswered
									: strings.showunanswered}
							</button>
						</p>
					)}
				</div>
			)
		);
	}
}

export default SubtestAnswers;
